/**
 * useFarmerSurfaces — data-aware farmer navigation flags.
 *
 * PILOT SCOPE: independent (G1) harvests were cut from the pilot, so the old
 * harvests-vs-contributions visibility rule (and its /harvests probe) is gone.
 * Pilot farmers are cooperative members, so "My contributions" is ALWAYS shown
 * for a farmer-like caller. The hook still resolves the farm profile so
 * consumers can tell whether the farmer is attached to a cooperative.
 *
 * Fail-open posture: GET /me/farm-profile 404s for non-farmer accounts and may
 * error offline; pending / errored resolves to `isInCoop=false` without hiding
 * any surface (showContributions does not depend on it).
 *
 * Cache-key sharing: ['me-farm-profile'] is the same key the Home / My farm
 * screens use, so nothing double-fetches.
 */
import { useQuery } from '@tanstack/react-query';

import { readAccessTokenClaims } from './client';
import { meControllerGetFarmProfile } from './generated/me/me';

export interface FarmerSurfaces {
  /** Still resolving the farm-profile probe. */
  isLoading: boolean;
  /** cooperativeId !== null. Fail-open to false on 404 / error. */
  isInCoop: boolean;
  /** Always true for an enabled (farmer-like) caller in the pilot. */
  showContributions: boolean;
}

export function useFarmerSurfaces(enabled = true): FarmerSurfaces {
  const sub = readAccessTokenClaims()?.sub;

  // `retry: false` so a 404 (non-farmer) or offline error resolves to the
  // fail-open default immediately. `enabled` lets a non-farmer caller (the
  // shared sidebar renders for every role) skip the farmer-scoped probe.
  const farmProfileQuery = useQuery({
    queryKey: ['me-farm-profile'],
    queryFn: ({ signal }) => meControllerGetFarmProfile(signal),
    retry: false,
    enabled: enabled && !!sub,
  });

  if (!enabled) {
    return { isLoading: false, isInCoop: false, showContributions: false };
  }

  return {
    isLoading: farmProfileQuery.isPending,
    isInCoop: farmProfileQuery.data?.cooperativeId != null,
    showContributions: true,
  };
}
